/**
 * Canvas rendering utilities
 */

import type { Shape, Particle } from './types';
import { getParticleAlpha } from '../gameLogic/updateParticle';

/**
 * Clears the entire canvas
 */
export function clearCanvas(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.clearRect(0, 0, width, height);
}

/**
 * Draws a shape on the canvas - geometry wars style (outlined with glow)
 */
export function drawShape(ctx: CanvasRenderingContext2D, shape: Shape): void {
  ctx.save();
  ctx.translate(shape.x, shape.y);
  ctx.rotate(shape.rotation);

  // Geometry wars style - neon outline with glow
  ctx.strokeStyle = shape.color;
  ctx.lineWidth = 3;
  ctx.shadowColor = shape.color;
  ctx.shadowBlur = 15;

  switch (shape.type) {
    case 'circle':
      drawCircle(ctx, shape.size / 2);
      break;
    case 'square':
      drawSquare(ctx, shape.size);
      break;
    case 'rectangle':
      drawRectangle(ctx, shape.size * 1.5, shape.size);
      break;
    case 'triangle':
      drawTriangle(ctx, shape.size / 2);
      break;
    case 'pentagon':
      drawPentagon(ctx, shape.size / 2);
      break;
  }

  ctx.restore();
}

function drawCircle(ctx: CanvasRenderingContext2D, radius: number): void {
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();
}

function drawSquare(ctx: CanvasRenderingContext2D, size: number): void {
  const half = size / 2;
  ctx.strokeRect(-half, -half, size, size);
}

function drawRectangle(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const halfW = width / 2;
  const halfH = height / 2;
  ctx.strokeRect(-halfW, -halfH, width, height);
}

function drawTriangle(ctx: CanvasRenderingContext2D, radius: number): void {
  ctx.beginPath();
  for (let i = 0; i < 3; i++) {
    const angle = (Math.PI * 2 * i) / 3 - Math.PI / 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
  ctx.stroke();
}

function drawPentagon(ctx: CanvasRenderingContext2D, radius: number): void {
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
  ctx.stroke();
}

/**
 * Draws the background grid as lines.
 * Single path call — much cheaper than iterating particle objects.
 * Lines at 0.25 opacity; intersections overlap for a subtle dot accent.
 */
export function drawBackgroundGrid(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  gridSize: number
): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();

  for (let x = gridSize; x < width; x += gridSize) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
  }

  for (let y = gridSize; y < height; y += gridSize) {
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
  }

  ctx.stroke();
  ctx.restore();
}

/**
 * Draws a single particle. Alpha follows the path-based fade curve.
 */
export function drawParticle(ctx: CanvasRenderingContext2D, particle: Particle): void {
  const alpha = getParticleAlpha(particle);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = particle.color;
  ctx.beginPath();
  ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * Draws all particles
 */
export function drawParticles(ctx: CanvasRenderingContext2D, particles: Particle[]): void {
  particles.forEach(particle => drawParticle(ctx, particle));
}

/**
 * Draws feedback text at a position
 */
export function drawFeedbackText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  alpha: number = 1
): void {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#FFD700';
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * Draws the progress bar
 */
export function drawProgressBar(
  ctx: CanvasRenderingContext2D,
  progress: number,
  width: number,
_height: number
): void {
  const barHeight = 10;

  // Background
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.fillRect(0, 0, width, barHeight);

  // Progress
  const urgency = 1 - progress;
  const red = Math.floor(255 * urgency);
  const green = Math.floor(255 * progress);
  ctx.fillStyle = `rgb(${red}, ${green}, 0)`;
  ctx.fillRect(0, 0, width * progress, barHeight);

  // Border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, width, barHeight);
}
